import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { CleaningShell } from "@/components/CleaningShell";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, PlayCircle, CheckCircle2, ShieldCheck, MessageSquareWarning, StickyNote, Shirt, Users, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ENXOVAL_LABELS = {
  cama_casal: "Cama de casal",
  cama_solteiro: "Cama de solteiro",
  colchao_casal: "Colchão de casal",
  colchao_solteiro: "Colchão de solteiro",
  lencol_casal: "Lençol de casal",
  lencol_solteiro: "Lençol de solteiro",
  fronha: "Fronha",
  travesseiro: "Travesseiro",
  toalha_banho: "Toalha de banho",
  toalha_rosto: "Toalha de rosto",
};

const LAVAGEM_ITENS = [
  "lencol_casal", "lencol_solteiro", "fronha", "toalha_banho", "toalha_rosto", "cobertor", "colcha",
];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function CleaningQuarto({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [hospedeAtual, setHospedeAtual] = useState(null);
  const [pousadaNome, setPousadaNome] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [obsTexto, setObsTexto] = useState("");
  const [obsTipo, setObsTipo] = useState("observacao");
  const [lavOpen, setLavOpen] = useState(false);
  const [lavForm, setLavForm] = useState({ item_key: "lencol_casal", periodicidade_dias: 7, observacao: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cleaning/quartos/${id}`);
      setDetail(data);
      // buscar dados de hóspede + pousada via lista (evita expor rota /reservas)
      const { data: lista } = await api.get("/cleaning/quartos");
      const meu = (lista || []).find((q) => q.id === id);
      setHospedeAtual(meu?.hospede_atual || null);
      setPousadaNome(meu?.pousada_nome || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar quarto.");
      navigate("/limpeza", { replace: true });
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const quarto = detail?.quarto;
  const ocorrencia = detail?.ocorrencia || {};
  const podeLiberar = !!detail?.pode_liberar;
  const status = quarto?.status;

  const doTransition = async (endpoint, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/cleaning/quartos/${id}/${endpoint}`);
      toast.success(okMsg);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível concluir a ação.");
    } finally { setBusy(false); }
  };

  const openObs = (tipo) => {
    setObsTipo(tipo); setObsTexto(""); setObsOpen(true);
  };
  const submitObs = async () => {
    if (!obsTexto.trim()) return toast.error("Escreva uma mensagem.");
    setBusy(true);
    try {
      const prefixo = obsTipo === "problema" ? "[PROBLEMA] " : "";
      await api.post(`/cleaning/quartos/${id}/observacoes`, { texto: prefixo + obsTexto.trim() });
      toast.success(obsTipo === "problema" ? "Problema registrado" : "Observação registrada");
      setObsOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar.");
    } finally { setBusy(false); }
  };

  const submitLavagem = async () => {
    if (!lavForm.item_key) return toast.error("Escolha o item.");
    setBusy(true);
    try {
      await api.post(`/cleaning/quartos/${id}/lavagens`, {
        item_key: lavForm.item_key,
        periodicidade_dias: Number(lavForm.periodicidade_dias || 7),
        observacao: lavForm.observacao,
      });
      toast.success("Lavagem registrada");
      setLavOpen(false);
      setLavForm({ item_key: "lencol_casal", periodicidade_dias: 7, observacao: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar lavagem.");
    } finally { setBusy(false); }
  };

  if (loading || !quarto) {
    return (
      <CleaningShell user={user} onLogout={onLogout} subtitle="Detalhes do quarto">
        <div className="py-16 text-center text-stone-500 text-sm" data-testid="cleaning-quarto-loading">Carregando…</div>
      </CleaningShell>
    );
  }

  return (
    <CleaningShell user={user} onLogout={onLogout} subtitle="Detalhes do quarto">
      <button
        onClick={() => navigate("/limpeza")}
        data-testid="btn-cleaning-back"
        className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#083A6B] mb-4"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-5 mb-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{pousadaNome || "—"}</div>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-serif text-3xl text-stone-900 leading-none">Quarto {quarto.numero}</div>
            <div className="text-xs text-stone-500 mt-1 capitalize">
              {quarto.tipo || "—"} · até {quarto.capacidade || 1} pax
            </div>
          </div>
          <StatusPill status={status} size="lg" testid="cleaning-quarto-status" />
        </div>

        {hospedeAtual ? (
          <div className="pt-3 border-t border-stone-100 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Hóspede atual</div>
            <div className="flex items-center gap-2 text-stone-800 font-medium">
              <Users size={13} /> {hospedeAtual.nome_curto || "—"}
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              {fmtDate(hospedeAtual.data_entrada)} → {fmtDate(hospedeAtual.data_saida)} · {hospedeAtual.num_pessoas || 1} pax
            </div>
          </div>
        ) : (
          <div className="pt-3 border-t border-stone-100 text-xs text-stone-500">
            Sem hóspede hospedado no momento.
          </div>
        )}
      </section>

      {/* Ações principais - botões grandes mobile-first */}
      <section className="grid grid-cols-1 gap-3 mb-5">
        {status === "aguardando_limpeza" && (
          <ActionBtn
            testid="btn-iniciar-limpeza"
            onClick={() => doTransition("iniciar", "Limpeza iniciada")}
            disabled={busy}
            icon={PlayCircle}
            color="bg-[#3671B0] hover:bg-[#2b5a8c]"
            label="INICIAR LIMPEZA"
            hint="Registrar que você começou a limpar este quarto"
          />
        )}
        {status === "em_limpeza" && (
          <ActionBtn
            testid="btn-finalizar-limpeza"
            onClick={() => doTransition("finalizar", "Limpeza finalizada")}
            disabled={busy}
            icon={CheckCircle2}
            color="bg-[#7B4FA1] hover:bg-[#653d87]"
            label="FINALIZAR LIMPEZA"
            hint="O quarto ficará aguardando liberação para uso"
          />
        )}
        {status === "aguardando_liberacao" && (
          podeLiberar ? (
            <ActionBtn
              testid="btn-liberar-quarto"
              onClick={() => doTransition("liberar", "Quarto liberado")}
              disabled={busy}
              icon={ShieldCheck}
              color="bg-[#083A6B] hover:bg-[#052B50]"
              label="LIBERAR QUARTO"
              hint="Só faça isso após conferir tudo — o quarto voltará a estar disponível"
            />
          ) : (
            <div className="border border-stone-200 bg-[#FDF7EF] text-stone-700 rounded-lg p-4 text-sm flex items-start gap-3" data-testid="cleaning-no-permission">
              <AlertTriangle size={18} className="text-[#8B6A1F] shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Aguardando liberação por outra pessoa autorizada.</div>
                <div className="text-xs text-stone-500 mt-1">Você não tem permissão para liberar este quarto. Um administrador ou colaboradora autorizada deve fazer isso.</div>
              </div>
            </div>
          )
        )}
        {(status === "disponivel" || status === "ocupado" || status === "manutencao") && (
          <div className="border border-stone-200 bg-[#FFFFFF] text-stone-600 rounded-lg p-4 text-sm">
            Este quarto não está em fila de limpeza no momento. Registre observações ou lavagens se necessário.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <SecondaryBtn
            testid="btn-informar-problema"
            onClick={() => openObs("problema")}
            icon={MessageSquareWarning}
            label="INFORMAR PROBLEMA"
            color="text-[#B4472B] border-[#B4472B]/30 hover:bg-[#FEE7DE]"
          />
          <SecondaryBtn
            testid="btn-observacao"
            onClick={() => openObs("observacao")}
            icon={StickyNote}
            label="OBSERVAÇÃO"
            color="text-stone-700 border-stone-300 hover:bg-stone-50"
          />
        </div>
        <SecondaryBtn
          testid="btn-enxoval"
          onClick={() => setLavOpen(true)}
          icon={Shirt}
          label="ENXOVAL / LAVAGEM"
          color="text-[#083A6B] border-[#083A6B]/30 hover:bg-[#EAF0EA]"
          full
        />
      </section>

      {/* Alertas de lavagem */}
      {(detail?.alertas_lavagem || []).length > 0 && (
        <section className="bg-[#FEE7DE] border border-[#B4472B]/20 rounded-lg p-4 mb-4" data-testid="alertas-lavagem">
          <div className="flex items-center gap-2 text-[#B4472B] font-semibold text-sm mb-2">
            <AlertTriangle size={14} /> Lavagens vencidas
          </div>
          <ul className="space-y-1 text-xs text-stone-700">
            {detail.alertas_lavagem.map((a, i) => (
              <li key={i}>
                <strong>{ENXOVAL_LABELS[a.item] || a.item}</strong> — venceu em {fmtDate(a.vencida_em)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Histórico da ocorrência */}
      <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-5 mb-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-3 flex items-center gap-1.5">
          <Clock size={11} /> Histórico da ocorrência atual
        </div>
        <ul className="space-y-2 text-sm">
          <HistoricoItem label="Check-out" data={ocorrencia.checkout_em} />
          <HistoricoItem label="Iniciou limpeza" data={ocorrencia.iniciou_em} por={ocorrencia.iniciou_por} />
          <HistoricoItem label="Finalizou limpeza" data={ocorrencia.finalizou_em} por={ocorrencia.finalizou_por} />
          <HistoricoItem label="Liberou quarto" data={ocorrencia.liberou_em} por={ocorrencia.liberou_por} />
        </ul>
        {(ocorrencia.observacoes || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Observações registradas</div>
            <ul className="space-y-2 text-xs text-stone-700">
              {ocorrencia.observacoes.map((o, i) => (
                <li key={i} className="border-l-2 border-stone-200 pl-2">
                  <div>{o.texto}</div>
                  <div className="text-[10px] text-stone-400 mt-0.5">{o.por || "—"} · {fmtDT(o.em)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Enxoval do quarto */}
      {detail?.enxoval && Object.keys(detail.enxoval).length > 0 && (
        <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-5 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-3 flex items-center gap-1.5">
            <Shirt size={11} /> Enxoval de referência
          </div>
          <ul className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(detail.enxoval).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between border border-stone-100 rounded px-2 py-1.5">
                <span className="text-stone-600">{ENXOVAL_LABELS[k] || k}</span>
                <span className="font-medium text-stone-800">{v}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Últimas lavagens */}
      {(detail?.lavagens_recentes || []).length > 0 && (
        <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-5 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-3">Últimas lavagens</div>
          <ul className="space-y-2 text-xs">
            {detail.lavagens_recentes.slice(0, 8).map((lv) => (
              <li key={lv.id} className="flex items-center justify-between border-b border-stone-100 pb-1.5 last:border-0">
                <div>
                  <div className="text-stone-800 font-medium">{ENXOVAL_LABELS[lv.item_key] || lv.item_key}</div>
                  <div className="text-[10px] text-stone-400">Por {lv.user_nome || "—"}</div>
                </div>
                <div className="text-right text-stone-500">
                  <div>Retirada: {fmtDate(lv.data_retirada)}</div>
                  <div className="text-[10px]">Próxima: {fmtDate(lv.proxima_lavagem_em)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Dialog observação/problema */}
      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent data-testid="dialog-obs">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {obsTipo === "problema" ? "Informar problema" : "Adicionar observação"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="obs-texto">Descreva</Label>
            <Textarea
              id="obs-texto"
              data-testid="input-obs-texto"
              rows={5}
              value={obsTexto}
              onChange={(e) => setObsTexto(e.target.value)}
              placeholder={obsTipo === "problema" ? "Ex: chuveiro sem pressão, tomada solta…" : "Ex: hóspede deixou objeto pessoal, colcha manchada…"}
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setObsOpen(false)} data-testid="btn-cancel-obs">Cancelar</Button>
            <Button onClick={submitObs} disabled={busy} className="bg-[#083A6B] hover:bg-[#052B50] text-white" data-testid="btn-save-obs">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog lavagem */}
      <Dialog open={lavOpen} onOpenChange={setLavOpen}>
        <DialogContent data-testid="dialog-lavagem">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Registrar lavagem</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Item</Label>
              <Select value={lavForm.item_key} onValueChange={(v) => setLavForm((f) => ({ ...f, item_key: v }))}>
                <SelectTrigger data-testid="select-lavagem-item"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LAVAGEM_ITENS.map((k) => (
                    <SelectItem key={k} value={k}>{ENXOVAL_LABELS[k] || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Periodicidade (dias)</Label>
              <Input
                type="number" min="1" max="60"
                data-testid="input-lavagem-dias"
                value={lavForm.periodicidade_dias}
                onChange={(e) => setLavForm((f) => ({ ...f, periodicidade_dias: e.target.value }))}
              />
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Input
                data-testid="input-lavagem-obs"
                value={lavForm.observacao}
                onChange={(e) => setLavForm((f) => ({ ...f, observacao: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setLavOpen(false)} data-testid="btn-cancel-lav">Cancelar</Button>
            <Button onClick={submitLavagem} disabled={busy} className="bg-[#083A6B] hover:bg-[#052B50] text-white" data-testid="btn-save-lav">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CleaningShell>
  );
}

function ActionBtn({ testid, onClick, disabled, icon: Icon, label, hint, color }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-left ${color} disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg p-4 flex items-center gap-4 shadow-sm transition-all active:scale-[0.99]`}
    >
      <Icon size={30} strokeWidth={1.6} className="shrink-0" />
      <div className="flex-1">
        <div className="font-semibold text-base tracking-wide leading-none">{label}</div>
        {hint && <div className="text-[11px] opacity-85 mt-1">{hint}</div>}
      </div>
    </button>
  );
}

function SecondaryBtn({ testid, onClick, icon: Icon, label, color, full }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`bg-white border rounded-lg py-3 px-3 flex items-center justify-center gap-2 text-xs font-semibold tracking-wide transition-all active:scale-[0.99] ${color} ${full ? "col-span-2" : ""}`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}

function HistoricoItem({ label, data, por }) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="text-stone-600 text-xs">{label}</div>
      <div className="text-right">
        <div className="text-stone-800">{data ? fmtDT(data) : <span className="text-stone-400">—</span>}</div>
        {por && <div className="text-[10px] text-stone-400">{por}</div>}
      </div>
    </li>
  );
}
